import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Translation } from '../../../translations'
import type { CitationSource, Message } from '../types'

interface ChatMessageProps {
    message: Message
    isOwn: boolean
    t: Translation
    onCopyAnswer: (text: string) => void
    onDownloadAnswer: (filename: string, content: string) => void
    onCopyLink: (url: string) => void
    expandedSources: Record<string, boolean>
    onToggleSource: (key: string) => void
    onRelatedQuestionClick: (question: string) => void
}

export function ChatMessage({
    message,
    isOwn,
    t,
    onCopyAnswer,
    onDownloadAnswer,
    onCopyLink,
    expandedSources,
    onToggleSource,
    onRelatedQuestionClick
}: ChatMessageProps) {
    const bubbleClasses = isOwn
        ? 'bg-blue-600 text-white rounded-br-none'
        : 'bg-slate-100 text-slate-800 rounded-bl-none'

    // Pre-process content to handle citations and fix markdown tables
    const contentWithCitations = useMemo(() => {
        if (!message.content) return ''
        let content = message.content

        // Ensure tables have a blank line before them
        // Fixes cases where AI outputs: "text [1] | col | col |" (same line)
        // matches non-newline AND non-pipe char, optional space, then table start (at least 2 consecutive columns like |...|...|)
        content = content.replace(/([^\n|])\s*(\|.+?\|.+?\|)/g, '$1\n\n$2')

        // Also ensure the separator row |---| is on its own line if wrongly merged with Header
        content = content.replace(/(\|)\s*(\| :?-+)/g, '$1\n$2')

        // Ensure the separator row is separated from the Body content if wrongly merged
        // Matches | :--- | :--- | followed immediately by | (start of next row)
        content = content.replace(/(\|\s*:?-+[-| :]*\|)\s*(\|)/g, '$1\n$2')

        // Replace [n] with [n](citation:n)
        return content.replace(/\[(\d+)\]/g, '[$1](citation:$1)')
    }, [message.content])

    const sources = useMemo(() => buildCitationIndexSources(message), [message])

    // Memoize plugins array to prevent re-renders
    const remarkPlugins = useMemo(() => [remarkGfm], [])

    // Custom renderer for citations
    const components = useMemo(() => ({
        // Table styling
        table: ({ node, ...props }: any) => (
            <div className="overflow-x-auto my-4 rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 bg-white" {...props} />
            </div>
        ),
        thead: ({ node, ...props }: any) => <thead className="bg-slate-50" {...props} />,
        th: ({ node, ...props }: any) => (
            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700" {...props} />
        ),
        tbody: ({ node, ...props }: any) => <tbody className="divide-y divide-slate-100 bg-white" {...props} />,
        tr: ({ node, ...props }: any) => <tr className="hover:bg-slate-50/50" {...props} />,
        td: ({ node, ...props }: any) => (
            <td className="px-3 py-2 text-sm text-slate-600 whitespace-pre-wrap" {...props} />
        ),

        // Link handling (including citations)
        a: ({ node, href, children, ...props }: any) => {
            if (href?.startsWith('citation:')) {
                const index = parseInt(href.replace('citation:', ''), 10)
                const source = sources[index - 1]
                const title = source ? `${source.title}${source.pageNumber ? ` (p. ${source.pageNumber})` : ''}` : undefined

                if (source?.link) {
                    return (
                        <a
                            href={withPdfPage(source.link, source.pageNumber)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mx-0.5 inline-flex h-[1.2em] min-w-[1.2em] items-center justify-center rounded-full border border-blue-200 bg-blue-100 text-[0.7rem] font-bold text-blue-700 hover:bg-blue-200 no-underline"
                            title={title}
                            {...props}
                        >
                            {children}
                        </a>
                    )
                }
                return (
                    <span
                        className="mx-0.5 inline-flex h-[1.2em] min-w-[1.2em] items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-[0.7rem] font-bold text-slate-600 cursor-help"
                        title={title || 'Source unavailable'}
                    >
                        {children}
                    </span>
                )
            }
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                    {...props}
                >
                    {children}
                </a>
            )
        },

        // List styling
        ul: ({ node, ...props }: any) => <ul className="list-disc pl-5 space-y-1 my-2" {...props} />,
        ol: ({ node, ...props }: any) => <ol className="list-decimal pl-5 space-y-1 my-2" {...props} />,
        li: ({ node, ...props }: any) => <li className="pl-1" {...props} />,
        p: ({ node, ...props }: any) => <p className="mb-2 last:mb-0 whitespace-pre-wrap leading-relaxed" {...props} />

    }), [sources])

    return (
        <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[92%] sm:max-w-[80%] rounded-2xl p-4 text-sm leading-relaxed ${bubbleClasses}`}>
                <div className="font-sans">
                    {isOwn ? (
                        <div className="whitespace-pre-wrap">{message.content}</div>
                    ) : (
                        <ReactMarkdown
                            remarkPlugins={remarkPlugins}
                            components={components}
                        >
                            {contentWithCitations}
                        </ReactMarkdown>
                    )}
                </div>

                {message.role === 'model' && (
                    <div className="mt-3 flex flex-wrap gap-2 text-xs print:hidden">
                        <button
                            type="button"
                            onClick={() => onCopyAnswer(message.content)}
                            className="rounded-full border border-slate-200 bg-white p-2 text-slate-700 hover:bg-slate-50 transition-colors"
                            aria-label={t.chatCopyAnswer}
                            title={t.chatCopyAnswer}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 18H8V7h11v16z" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={() => onDownloadAnswer('answer.txt', message.content)}
                            className="rounded-full border border-slate-200 bg-white p-2 text-slate-700 hover:bg-slate-50 transition-colors"
                            aria-label={t.chatDownload}
                            title={t.chatDownload}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                                <path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z" />
                            </svg>
                        </button>
                    </div>
                )}

                {message.role === 'model' && message.related_questions?.length ? (
                    <div className="mt-4 border-t border-slate-200/50 pt-3">
                        <p className="mb-2 text-xs font-semibold text-slate-500">{t.chatRelated}</p>
                        <div className="flex flex-wrap gap-2">
                            {message.related_questions.map((question, index) => (
                                <button
                                    key={`${question}-${index}`}
                                    type="button"
                                    onClick={() => onRelatedQuestionClick(question)}
                                    className="text-left text-xs text-blue-700 transition-colors hover:bg-blue-50 rounded-full border border-blue-200 bg-white px-3 py-1.5 shadow-sm"
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}

                {message.role === 'model' && message.context_used ? (
                    <details className="mt-2 border-t border-slate-200/50 pt-2 text-xs opacity-70">
                        <summary className="cursor-pointer font-semibold hover:underline">{t.chatContext}</summary>
                        <div className="mt-1 max-h-40 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2 font-mono whitespace-pre-wrap">
                            {renderContextSources(message)}
                        </div>
                    </details>
                ) : null}

                {message.role === 'model'
                    ? renderSources(message, t, onCopyLink, expandedSources, onToggleSource)
                    : null}
            </div>
        </div>
    )
}

function buildCitationIndexSources(message: Message): CitationSource[] {
    const byId = new Map<string, CitationSource>()
    const ordered: CitationSource[] = []

    const addSource = (source: CitationSource | undefined) => {
        if (!source?.id) return
        if (byId.has(source.id)) {
            const existing = byId.get(source.id)!
            byId.set(source.id, {
                id: existing.id,
                title: existing.title || source.title,
                link: existing.link || source.link,
                pageNumber: existing.pageNumber || source.pageNumber,
                chunks: existing.chunks || source.chunks
            })
            return
        }
        byId.set(source.id, source)
        ordered.push(source)
    }

    for (const src of message.results || []) addSource(src)
    for (const citation of message.citations || []) {
        for (const src of citation.sources || []) addSource(src)
    }

    return ordered.map(src => byId.get(src.id) || src)
}

function renderSources(
    message: Message,
    t: Translation,
    onCopyLink: (url: string) => void,
    expandedSources: Record<string, boolean>,
    onToggleSource: (key: string) => void
) {
    if (!message.results || message.results.length === 0) return null

    return (
        <details className="mt-3 border-t border-slate-200/50 pt-3 text-xs">
            <summary className="cursor-pointer font-semibold text-slate-600 hover:underline">
                {t.chatSources} ({message.results.length})
            </summary>
            <div className="mt-2 space-y-2">
                {message.results.map((source) => {
                    const key = `${message.role}-${source.id}`
                    const isExpanded = expandedSources[key]
                    return (
                        <div key={source.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-2">
                            <div className="min-w-0 flex-1">
                                <a
                                    href={withPdfPage(source.link, source.pageNumber)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-700 hover:underline wrap-break-word"
                                    title={`${source.title}${source.pageNumber ? ` (p. ${source.pageNumber})` : ''}`}
                                >
                                    {source.title}
                                    {source.pageNumber ? <span className="ml-2 text-slate-500">p. {source.pageNumber}</span> : null}
                                </a>

                                {source.chunks?.length && isExpanded ? (
                                    <div className="mt-2 whitespace-pre-wrap text-xs text-slate-700">
                                        {source.chunks
                                            .map(chunk => `${chunk.pageNumber ? `p. ${chunk.pageNumber}: ` : ''}${chunk.content}`)
                                            .join('\n\n')}
                                    </div>
                                ) : null}
                            </div>

                            <div className="flex shrink-0 items-center gap-2">
                                {source.chunks?.length ? (
                                    <button
                                        type="button"
                                        onClick={() => onToggleSource(key)}
                                        className="rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-700 hover:bg-slate-100"
                                        aria-label="More"
                                        title="More"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                                            <path d="M12 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
                                        </svg>
                                    </button>
                                ) : null}

                                <button
                                    type="button"
                                    onClick={() => onCopyLink(withPdfPage(source.link, source.pageNumber))}
                                    className="rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-700 hover:bg-slate-100"
                                    aria-label={t.chatCopy}
                                    title={t.chatCopy}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 18H8V7h11v16z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )
                })}
            </div>
        </details>
    )
}

function renderContextSources(message: Message) {
    if (!message.context_used) return null

    const titles = message.context_used
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => line.replace(/^Source:\s*/i, ''))

    if (titles.length === 0) {
        return <div className="font-mono whitespace-pre-wrap">{message.context_used}</div>
    }

    const sources = message.results || []
    const resolver = (title: string) =>
        sources.find(src => src.title === title) || sources.find(src => src.title?.toLowerCase() === title.toLowerCase())

    return (
        <div className="space-y-1">
            {titles.map((title, index) => {
                const source = resolver(title)
                if (!source?.link) {
                    return (
                        <div key={`${title}-${index}`} className="font-mono">
                            {title}
                        </div>
                    )
                }
                return (
                    <a
                        key={`${source.id}-${index}`}
                        href={withPdfPage(source.link, source.pageNumber)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block font-mono text-blue-700 hover:underline wrap-break-word"
                        title={`${source.title}${source.pageNumber ? ` (p. ${source.pageNumber})` : ''}`}
                    >
                        {title}
                    </a>
                )
            })}
        </div>
    )
}

function withPdfPage(url: string, pageNumber?: string | null) {
    if (!url || !pageNumber) return url
    const page = Number.parseInt(String(pageNumber), 10)
    if (!Number.isFinite(page) || page <= 0) return url
    return url.includes('#') ? `${url}&page=${page}` : `${url}#page=${page}`
}
